import { useState } from 'react';
import { MutualFundTableData, MutualFundTableItem } from '../types/mutualFund';

interface MutualFundTableProps {
  data: MutualFundTableData;
}

export default function MutualFundTable({ data }: MutualFundTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = Number(e.target.value);
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const formatDollar = (value: number | null, position?: number, unit?: string) => {
    return value === null ? '-' : position
      ? `$${Number(value/position).toFixed(2)}${unit || ''}`
      : `$${Number(value).toFixed(2)}${unit || ''}`;
  };

  const formatCurrency = (value: number | null | undefined, precision?: number) => {
    if (value === null || value === undefined) return '-';
    const num = precision ? Number(value).toFixed(precision) : Number(value).toString();
    return addCommas(num);
  };

  const addCommas = (num: string) => {
    const parts = num.split('.');
    parts[0] = parts[0].replace(/(\d{1,3})(?=(?:\d{3})+(?!\d))/g, '$1,');
    return parts.length > 1 ? parts[0] + '.' + parts[1] : parts[0];
  };

  const copy = (obj: MutualFundTableData)=>{
    return JSON.parse(JSON.stringify(obj));
  }

  // Calculate pagination
  const total = data.length;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  // 修复首行rowspan和colspan都是0时，不显示日期的bug
  const currentData = copy(data.slice(startIndex, endIndex));
  if(currentData.length > 0){
    if(currentData[0].itemRowSpan === 0){
      currentData[0].itemRowSpan = currentData.findIndex((x: MutualFundTableItem) => x.itemRowSpan !==0);
      currentData[0].itemColSpan = 1;
    }
  }

  return (
    <div className="mt-4">
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="text-xs sm:text-sm md:text-base lg:text-lg pt-3 pb-3 overflow-hidden text-ellipsis whitespace-nowrap font-medium text-white">
              <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 bg-[#444648] text-left border border-gray-800 rounded-tl-md sm:rounded-tl-lg md:rounded-tl-xl">Date</th>
              <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 bg-[#444648] text-left border border-gray-800">Price</th>
              <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 bg-[#444648] text-left border border-gray-800">Ticker</th>
              <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 bg-[#444648] text-left border border-gray-800">Share Class</th>
              <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 bg-[#444648] text-left border border-gray-800">Shares</th>
              <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 bg-[#444648] text-left border border-gray-800 rounded-tr-md sm:rounded-tr-lg md:rounded-tr-xl">Share Name</th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((item:MutualFundTableItem, index:number) => (
              <tr
                key={item.itemId || index}
                className="hover:bg-[#333434] text-white"
              >
                {item.itemRowSpan > 0 && (
                  <td
                    className="px-2 sm:px-4 md:px-6 py-1 sm:py-2 whitespace-nowrap border border-gray-800 text-left text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]"
                    rowSpan={item.itemRowSpan}
                  >
                    {item.reportDate}
                  </td>
                )}
                <td className="px-2 sm:px-4 md:px-6 py-1 sm:py-2 whitespace-nowrap border border-gray-800 text-left text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]">
                  {formatDollar(item.price)}
                </td>
                <td className="px-2 sm:px-4 md:px-6 py-1 sm:py-2 whitespace-nowrap border border-gray-800 text-left text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]">
                  {item.ticker}
                </td>
                <td className="px-2 sm:px-4 md:px-6 py-1 sm:py-2 whitespace-nowrap border border-gray-800 text-left text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]">
                  {item.shareClass}
                </td>
                <td className="px-2 sm:px-4 md:px-6 py-1 sm:py-2 whitespace-nowrap border border-gray-800 text-left text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]">
                  {formatCurrency(item.price)}
                </td>
                <td className="px-2 sm:px-4 md:px-6 py-1 sm:py-2 whitespace-nowrap border border-gray-800 text-left text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]">
                  {item.shareDescription}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 text-white">
        <div className="flex items-center space-x-2 text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]">
          <span>每页</span>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className="bg-[#171717] border border-[#444648] rounded px-2 py-1"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
          </select>
          <span>条</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-[#444648] rounded disabled:opacity-50 text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]"
          >
            上一页
          </button>
          <span className="text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]">
            {currentPage} / {Math.ceil(total / pageSize)}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(total / pageSize)))}
            disabled={currentPage === Math.ceil(total / pageSize)}
            className="px-3 py-1 border border-[#444648] rounded disabled:opacity-50 text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]"
          >
            下一页
          </button>
        </div>
      </div>
    </div>
  );
}
